import { appStorage } from '../lib/betaStorage'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { YouthNixiVideo } from '../components/YouthNixiVideo'
import { useApp } from '../store/AppContext'
import { loadYouthSupportPlan } from '../lib/youthSupportPlan'
import { localISODate } from '../lib/date'
import { awardFoodBites, availableFoodBites, earnedChildPoints, loadCompanionState } from '../lib/youthRewards'
import { isYouthDemoProfile, youthDemoProfile } from '../lib/youthDemo'

function doneKey(participantId: string, tileId: string, stepId: string) {
  return `schub.youthTileDone.${participantId}.${localISODate()}.${tileId}.${stepId}`
}

function legacyDoneKey(tileId: string, stepId: string) {
  return `schub.youthTileDone.${localISODate()}.${tileId}.${stepId}`
}

function bodyDoubleFor(text: string) {
  const value = text.toLowerCase()
  if (value.includes('make my bed')) return { message: 'Let’s make our beds!' }
  if (value.includes('teeth')) return { message: 'I need to brush my teeth too! Have you brushed yours?' }
  if (value.includes('breakfast') || value.includes('snack') || value.includes('eat')) return { message: 'Let’s eat! I need to eat too!' }
  if (value.includes('pjs')) return { message: 'PJs time! I’m getting ready for bed with you.' }
  if (value.includes('dress')) return { message: 'I’m ready too!' }
  if (value.includes('book') || value.includes('read')) return { message: 'I want to read too! What are we reading?' }
  if (value.includes('school bag') || value.includes('bag ready')) return { message: 'Let’s go!' }
  if (value.includes('bag') || value.includes('pack')) return { message: 'Let’s get ready together!' }
  if (value.includes('sleep')) return { message: 'I’m sleepy too. Goodnight!' }
  return { message: 'I’m doing this with you. Let’s do the next step together!' }
}

function actionForStep(id: string, text: string) {
  if (id === 'morning-bed') return { message: 'Let’s make our beds!' }
  if (id === 'morning-dress') return { message: 'I’m ready too!' }
  if (id === 'morning-teeth' || id === 'bed-teeth') return { message: 'I need to brush my teeth too! Have you brushed yours?' }
  if (id === 'morning-breakfast' || id === 'home-snack') return { message: 'Let’s eat! I need to eat too!' }
  if (id === 'morning-bag') return { message: 'Let’s go!' }
  if (id === 'bed-book') return { message: 'I want to read too! What are we reading?' }
  if (id === 'bed-sleep') return { message: 'I’m sleepy too. Goodnight!' }
  return bodyDoubleFor(text)
}

function isSchoolBagReadyStep(id: string, text: string) {
  const value = text.toLowerCase()
  return id === 'morning-bag' || value.includes('school bag') || value.includes('preschool bag') || value.includes('bag ready')
}

export function YouthRoutineDetail() {
  const { currentUser } = useApp()
  const participantId = currentUser.id === 'u-jnr' ? 'u-jnr' : 'u-leo'
  const { tileId } = useParams()
  const [plan, setPlan] = useState(() => loadYouthSupportPlan(participantId))
  const [, setVersion] = useState(0)
  const [nixiMessage, setNixiMessage] = useState('I’m ready! What are we doing first?')
  const [blowingKisses, setBlowingKisses] = useState(false)
  const [routineCelebration, setRoutineCelebration] = useState(false)
  const [celebrationVersion, setCelebrationVersion] = useState(0)
  const [nixiFlying, setNixiFlying] = useState(false)
  const [nixiWavingGoodnight, setNixiWavingGoodnight] = useState(false)
  const celebrationTimer = useRef<number | null>(null)
  const flightTimer = useRef<number | null>(null)
  const goodnightTimer = useRef<number | null>(null)
  const todayWeekday = new Date().getDay()
  const [selectedWeekday, setSelectedWeekday] = useState(todayWeekday >= 1 && todayWeekday <= 5 ? todayWeekday : 1)
  const companion = loadCompanionState(participantId)
  const bites = availableFoodBites(participantId)
  const points = earnedChildPoints(participantId)
  const childName = isYouthDemoProfile() ? youthDemoProfile.name : currentUser.name.split(' ')[0]

  useEffect(() => {
    const refresh = () => setPlan(loadYouthSupportPlan(participantId))
    window.addEventListener('schub:youth-support-plan', refresh)
    return () => window.removeEventListener('schub:youth-support-plan', refresh)
  }, [participantId])

  useEffect(() => () => {
    if (celebrationTimer.current !== null) window.clearTimeout(celebrationTimer.current)
    if (flightTimer.current !== null) window.clearTimeout(flightTimer.current)
    if (goodnightTimer.current !== null) window.clearTimeout(goodnightTimer.current)
  }, [])

  const tile = plan.tiles.find((item) => item.id === tileId)
  const schoolDay = tile?.schoolDays?.find((day) => day.weekday === selectedWeekday) ?? tile?.schoolDays?.[0]
  const steps = useMemo(() => tile?.kind === 'school' ? schoolDay?.pack ?? [] : tile?.steps ?? [], [tile, schoolDay])

  if (!tile) return <div className="min-h-screen bg-slate-50 p-5"><Link to="/today" className="font-black text-violet-700">← Back</Link><p className="mt-6 font-black">This tile is not available.</p></div>

  const isDone = (id: string) => {
    try {
      const scopedKey = doneKey(currentUser.id, tile.id, id)
      if (appStorage.getItem(scopedKey) === '1') return true
      const legacyKey = legacyDoneKey(tile.id, id)
      if (appStorage.getItem(legacyKey) !== '1') return false
      appStorage.setItem(scopedKey, '1')
      appStorage.removeItem(legacyKey)
      return true
    } catch { return false }
  }

  const toggle = (id: string, text: string) => {
    const key = doneKey(currentUser.id, tile.id, id)
    let completing = true
    try {
      completing = appStorage.getItem(key) !== '1'
      if (completing) appStorage.setItem(key, '1')
      else appStorage.removeItem(key)
    } catch { /* prototype still works */ }

    if (completing) {
      const awarded = awardFoodBites(participantId, 'routine_step', `${tile.id}:${id}:${localISODate()}`, 1)
      const action = actionForStep(id, text)

      if (id === 'bed-goodnight') {
        setBlowingKisses(true)
        setNixiMessage('Mwah! Goodnight everyone! 💕')
        window.setTimeout(() => setBlowingKisses(false), 2600)
      } else if (id === 'bed-sleep') {
        setBlowingKisses(false)
        setNixiMessage(`Goodnight ${childName}, see you tomorrow!`)
      } else {
        setNixiMessage(awarded ? `We did it! +1 point for you and +1 Food Bite for me! ${action.message}` : action.message)
      }

      setCelebrationVersion((value) => value + 1)
      setNixiWavingGoodnight(id === 'bed-sleep')

      if (id === 'bed-sleep') {
        setRoutineCelebration(false)
        if (goodnightTimer.current !== null) window.clearTimeout(goodnightTimer.current)
        goodnightTimer.current = window.setTimeout(() => setNixiWavingGoodnight(false), companion.colour === 'pink' ? 5000 : 1600)
      } else {
        setRoutineCelebration(true)
        if (celebrationTimer.current !== null) window.clearTimeout(celebrationTimer.current)
        celebrationTimer.current = window.setTimeout(() => setRoutineCelebration(false), companion.colour === 'pink' ? 4200 : 2000)
      }

      if (isSchoolBagReadyStep(id, text)) {
        setNixiFlying(true)
        if (flightTimer.current !== null) window.clearTimeout(flightTimer.current)
        flightTimer.current = window.setTimeout(() => setNixiFlying(false), companion.colour === 'pink' ? 4200 : 2000)
      }
    } else {
      setRoutineCelebration(false)
      setNixiFlying(false)
      setNixiWavingGoodnight(false)
    }
    setVersion((value) => value + 1)
  }

  const currentStep = steps.find((step) => !isDone(step.id))
  const inviteNixi = () => {
    if (!currentStep) return
    const action = actionForStep(currentStep.id, currentStep.text)
    setNixiMessage(action.message)
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-100 via-white to-sky-50 pb-28">
      <style>{`
        @keyframes nixi-heart-float { 0% { opacity: 0; transform: translate(0, 12px) scale(.65); } 18% { opacity: 1; } 100% { opacity: 0; transform: translate(var(--heart-x), -70px) scale(1.25); } }
        @keyframes nixi-star-sweep { 0% { opacity: 0; transform: translateX(-15vw) translateY(var(--star-y)) rotate(0deg) scale(.65); } 12% { opacity: 1; } 82% { opacity: 1; } 100% { opacity: 0; transform: translateX(115vw) translateY(calc(var(--star-y) - 35px)) rotate(540deg) scale(1.15); } }
        .nixi-heart { position: absolute; z-index: 20; animation: nixi-heart-float 1.8s ease-out forwards; pointer-events: none; }
        .nixi-star { position: fixed; left: 0; top: 12%; z-index: 50; pointer-events: none; animation: nixi-star-sweep 2.8s ease-in-out forwards; filter: drop-shadow(0 0 8px rgba(250,204,21,.55)); }
      `}</style>
      {routineCelebration && <div aria-hidden="true">
        <span className="nixi-star text-4xl" style={{ '--star-y': '4vh' } as React.CSSProperties}>⭐</span>
        <span className="nixi-star text-3xl" style={{ '--star-y': '18vh', animationDelay: '.18s' } as React.CSSProperties}>✨</span>
        <span className="nixi-star text-5xl" style={{ '--star-y': '34vh', animationDelay: '.32s' } as React.CSSProperties}>⭐</span>
        <span className="nixi-star text-3xl" style={{ '--star-y': '50vh', animationDelay: '.5s' } as React.CSSProperties}>✨</span>
        <span className="nixi-star text-4xl" style={{ '--star-y': '66vh', animationDelay: '.68s' } as React.CSSProperties}>⭐</span>
      </div>}
      <div className="px-4 pt-5">
        <div className="flex items-center"><Link to="/today" className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-xl font-black shadow-card">←</Link></div>
        <div className="mt-4 rounded-[2rem] bg-white p-5 shadow-card"><span className="text-5xl">{tile.icon}</span><h1 className="mt-3 text-3xl font-black text-slate-900">{tile.title}</h1><p className="mt-1 text-base font-bold text-slate-500">{tile.kind === 'school' ? 'What do I need to pack today?' : tile.subtitle}</p></div>
      </div>

      <div className="px-4 py-4">
        <button onClick={inviteNixi} className="relative mb-4 flex w-full items-center gap-3 overflow-hidden rounded-[2rem] bg-gradient-to-r from-violet-200 via-pink-100 to-amber-100 p-4 text-left shadow-card">
          <div className="relative shrink-0">
            <div className="aspect-[55/67] w-24 overflow-hidden rounded-[1.35rem] bg-violet-100 shadow-sm sm:w-28"><YouthNixiVideo className="h-full w-full object-cover" label={`Nixi in the ${tile.title} routine`} variant={nixiWavingGoodnight ? 'wave' : nixiFlying ? 'flying' : routineCelebration ? 'celebrate' : 'subtle'} colour={companion.colour} restartToken={celebrationVersion} /></div>
            {blowingKisses && <><span className="nixi-heart left-[52px] top-[35px] text-2xl" style={{ '--heart-x': '-28px' } as React.CSSProperties}>💗</span><span className="nixi-heart left-[62px] top-[40px] text-xl" style={{ '--heart-x': '10px', animationDelay: '.25s' } as React.CSSProperties}>💕</span><span className="nixi-heart left-[48px] top-[44px] text-lg" style={{ '--heart-x': '34px', animationDelay: '.5s' } as React.CSSProperties}>💖</span></>}
          </div>
          <div className="min-w-0 flex-1"><p className="font-black text-violet-950">Nixi is doing it with me</p><p className="mt-1 text-sm font-bold text-violet-800">{nixiMessage}</p><div className="mt-2 flex gap-2 text-xs font-black text-violet-700"><span>⭐ {points} points</span><span>🍪 {bites} bites</span></div></div>
        </button>

        {tile.kind === 'school' && <div className="mb-4 rounded-[2rem] bg-white p-4 shadow-card"><p className="mb-3 text-sm font-black text-slate-700">Choose a day</p><div className="grid grid-cols-5 gap-2">{tile.schoolDays?.map((day) => <button key={day.weekday} onClick={() => setSelectedWeekday(day.weekday)} className={`rounded-2xl px-2 py-3 text-xs font-black ${selectedWeekday === day.weekday ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-600'}`}>{day.label.slice(0, 3)}</button>)}</div></div>}
        {tile.kind === 'school' && schoolDay && <div className="mb-4 rounded-[2rem] bg-sky-100 p-5 shadow-card"><div className="flex items-start justify-between gap-3"><div><p className="text-sm font-black text-sky-700">{schoolDay.label}</p><p className="mt-1 text-2xl font-black text-sky-950">{schoolDay.activity}</p></div>{schoolDay.weekday === todayWeekday && <span className="rounded-full bg-white px-3 py-2 text-xs font-black text-sky-700">TODAY</span>}</div><p className="mt-3 text-sm font-bold text-sky-800">What do I need to pack?</p></div>}

        <div className="flex flex-col gap-3">{steps.map((step) => { const done = isDone(step.id); return <button key={step.id} onClick={() => toggle(step.id, step.text)} className={`flex min-h-20 items-center gap-4 rounded-3xl border-2 p-4 text-left shadow-card transition active:scale-[0.99] ${done ? 'border-emerald-300 bg-emerald-50' : 'border-white bg-white'}`}><span className="text-3xl">{step.icon}</span><span className={`flex-1 text-lg font-black ${done ? 'text-emerald-900 line-through' : 'text-slate-900'}`}>{step.text}</span><span className={`flex h-10 w-10 items-center justify-center rounded-full border-2 text-lg font-black ${done ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300 text-slate-300'}`}>✓</span></button> })}</div>
      </div>
    </div>
  )
}

